import { Link } from 'react-router-dom';
import { Beer, Utensils, Clock, Sparkles } from 'lucide-react';
import { Card, CardContent, Badge } from '@/components/ui';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useFilters } from '@/contexts/FilterContext';
import type { Special } from '@/api/endpoints/bars';

interface SpecialCardProps {
  special: Special & { barName?: string; barId?: string; barLogoUrl?: string; distance?: number };
  showBar?: boolean;
}

// Format price with $ prefix if not already present
function formatPrice(price: string | null): string | undefined {
  if (!price) return undefined;
  const trimmed = price.trim();
  if (trimmed.startsWith('$')) return trimmed;
  return `$${trimmed}`;
}

// Format HH time range (e.g., "16:00" → "4pm", "10:30" → "10:30am")
function formatTimeRange(
  start: string | null,
  end: string | null
): string | null {
  if (!start && !end) return null;
  const formatTime = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const mins = m > 0 ? `:${m.toString().padStart(2, '0')}` : '';
    if (h === 0) return `12${mins}am`;
    if (h === 12) return `12${mins}pm`;
    return h > 12 ? `${h - 12}${mins}pm` : `${h}${mins}am`;
  };
  if (!start) return `Open-${formatTime(end!)}`;
  if (!end) return `${formatTime(start)}-Close`;
  return `${formatTime(start)}-${formatTime(end)}`;
}

// Format distance in miles
function formatDistance(meters: number | undefined): string | null {
  if (meters === undefined) return null;
  const miles = meters / 1609;
  return miles < 0.1 ? '< 0.1 mi' : `${miles.toFixed(1)} mi`;
}

export function SpecialCard({ special, showBar = true }: SpecialCardProps) {
  const { isFavorite } = useUserPreferences();
  const { locationSource } = useFilters();
  const barIsFavorite = special.barId ? isFavorite(special.barId) : false;
  const TypeIcon =
    special.type === 'drink'
      ? Beer
      : special.type === 'food'
        ? Utensils
        : Sparkles;
  const formattedPrice = formatPrice(special.price);
  const hhTimeRange = formatTimeRange(
    special.happyHourStart,
    special.happyHourEnd
  );
  // Only show distance when using GPS (not when viewing a city page)
  const distanceText =
    locationSource === 'gps' ? formatDistance(special.distance) : null;

  const cardContent = (
    <Card
      lift
      className={`overflow-hidden ${barIsFavorite ? 'card-favorite' : ''}`}
    >
      <CardContent className="relative p-3 overflow-hidden">
        {/* Background logo - faded, right-aligned */}
        {special.barLogoUrl && (
          <img
            src={special.barLogoUrl}
            alt=""
            aria-hidden="true"
            className="card-logo-bg"
          />
        )}
        {/* Content */}
        <div className="relative z-10 flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-primary/10">
            <TypeIcon className="h-5 w-5 text-accent-primary" />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                <h3 className="truncate text-lg font-semibold">
                  {special.name}
                </h3>
                {special.isHappyHour && (
                  <Badge
                    variant="warning"
                    className="flex shrink-0 items-center gap-1"
                  >
                    <Clock className="h-3 w-3" />
                    {hhTimeRange || 'HH'}
                  </Badge>
                )}
              </div>
              {formattedPrice && (
                <span className="shrink-0 text-lg font-bold text-accent-primary">
                  {formattedPrice}
                </span>
              )}
            </div>
            {special.description && (
              <p className="line-clamp-1 text-xs text-muted">
                {special.description}
              </p>
            )}
            {showBar && special.barName && (
              <p className="mt-1 text-xs text-muted">
                at {special.barName}
                {distanceText && ` • ${distanceText}`}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Wrap in Link if we have a bar to navigate to
  if (showBar && special.barId) {
    return (
      <Link to={`/bars/${special.barId}`} className="block">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
